import React from 'react'
import PropTypes from 'prop-types'
import { useIntl, FormattedMessage } from 'react-intl'
import {
    Container,
    Box,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import LinksList from '../../links-list'
import {renderComponentsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import {useForYouNavColor} from '../../../utils/fyp-flags'
import {
    ITEMS_PER_COLUMN,
    MAX_CONTENT_GROUPS,
    TOTAL_GRID_COLUMNS,
    COLS_PER_GROUP,
    getCategoryUrl,
    getImageColSpan
} from './list-menu-helpers'

/**
 * Default API-driven mega menu content with 12-column grid layout.
 *
 * - L1 column and each L2 separate column each span 2 grid columns.
 * - Groups with more than 15 items overflow into an adjacent column (max 2 columns per group).
 * - Images are placed inline in the grid with colSpan based on IllustrationWidth.
 * - Divider guard: separators are not rendered on the last item in a column.
 */
const DefaultMegaMenuContent = ({ item, itemsKey, onClose, baseStyle, categories }) => {
    const intl = useIntl()
    const getForYouNavColor = useForYouNavColor()
    const items = item?.[itemsKey] || []

    const withForYouColor = (category, replacement) => {
        const forYouColor = getForYouNavColor(category)
        return forYouColor ? {...replacement, TextColor: forYouColor} : replacement
    }

    const l1CategoryId = items[0]?.parentCategoryId
    const l1CategoryName = items[0]?.parentCategoryTree?.find(
        (treeItem) => treeItem.id === l1CategoryId
    )?.name

    const l1CategoryObject = categories && l1CategoryId ? categories[l1CategoryId] : null

    const allXL1 = l1CategoryObject
        ? {
            ...l1CategoryObject,
            name: l1CategoryName && intl.formatMessage(
                { id: 'nav_alXL1.label', defaultMessage: 'All {l1CategoryName}' },
                { l1CategoryName }
            )
        }
        : {
            id: l1CategoryId,
            name: l1CategoryName && intl.formatMessage(
                { id: 'nav_alXL1.label', defaultMessage: 'All {l1CategoryName}' },
                { l1CategoryName }
            )
        }

    const allXL1CategoryReplacement = allXL1?.CategoryReplacement?.ContentLink?.Expanded

    const l1Heading = l1CategoryObject?.c_showAllOnL2
        ? {
            cmsCategoryReplacement: allXL1CategoryReplacement,
            href: getCategoryUrl(allXL1, categories),
            text: allXL1.name,
            wrapperStyles: baseStyle.allCategoryLinkWrapper
        }
        : ''

    const MegaMenuIlustrationBlockList =
        item?.MegaMenuIlustrationBlockList ||
        item?.CategoryReplacement?.ContentLink?.Expanded?.MenuCategoryIllustrationsBlock?.Expanded?.Illustrations

    // Calculate grid columns reserved by images to determine available group slots
    const imageColsUsed = MegaMenuIlustrationBlockList
        ? MegaMenuIlustrationBlockList.reduce((sum, block) => sum + getImageColSpan(block), 0)
        : 0

    const availableForGroups = TOTAL_GRID_COLUMNS - imageColsUsed
    const maxGroups = Math.min(MAX_CONTENT_GROUPS, Math.floor(availableForGroups / COLS_PER_GROUP))

    // L1 column: apply 15-item overflow
    const l1MainItems = items.slice(0, ITEMS_PER_COLUMN)
    const l1OverflowItems = items.slice(ITEMS_PER_COLUMN, ITEMS_PER_COLUMN * 2)
    const l1HasOverflow = l1OverflowItems.length > 0

    const l1links = l1MainItems.map((item) => {
        const { name } = item
        const cmsCategoryReplacement = withForYouColor(
            item,
            item?.CategoryReplacement?.ContentLink?.Expanded
        )
        return {
            href: getCategoryUrl(item, categories),
            text: name,
            cmsCategoryReplacement,
            c_showSeparatorMobile: item?.c_showSeparatorMobile,
            c_showSeparator: item?.c_showSeparator
        }
    })

    const l1OverflowLinks = l1OverflowItems.map((item) => {
        const { name } = item
        const cmsCategoryReplacement = withForYouColor(
            item,
            item?.CategoryReplacement?.ContentLink?.Expanded
        )
        return {
            href: getCategoryUrl(item, categories),
            text: name,
            cmsCategoryReplacement,
            c_showSeparatorMobile: item?.c_showSeparatorMobile,
            c_showSeparator: item?.c_showSeparator
        }
    })

    // L1 uses 1 slot (+ 1 more if it overflows and space allows)
    let usedSlots = 1 + (l1HasOverflow && maxGroups > 1 ? 1 : 0)

    // Build L2 separate column grid items with 15-item overflow and divider guard
    const l2GridItems = []
    const separateColumnItems = items.filter((item) => item.c_showOnSeparateColumn)

    for (const l2Item of separateColumnItems) {
        if (usedSlots >= maxGroups) break

        const { id, name: l2CategoryName, c_showSeparator: l2Separator } = l2Item
        const l2Items = l2Item[itemsKey] || []
        const cmsCategoryReplacement = l2Item?.CategoryReplacement?.ContentLink?.Expanded

        const mainL2Items = l2Items.slice(0, ITEMS_PER_COLUMN)
        const overflowL2Items = l2Items.slice(ITEMS_PER_COLUMN, ITEMS_PER_COLUMN * 2)
        const l2HasOverflow = overflowL2Items.length > 0

        const l2Heading = l2Item?.c_showAllOnL2
            ? {
                href: getCategoryUrl(l2Item, categories),
                text: intl.formatMessage(
                    { id: 'nav_alXL2.label', defaultMessage: 'All {l2CategoryName}' },
                    { l2CategoryName }
                ),
                wrapperStyles: l2Separator ? { ...baseStyle.linkSeparator } : undefined,
                cmsCategoryReplacement: withForYouColor(l2Item, cmsCategoryReplacement)
            }
            : ''

        // Divider guard: do not apply separator style to the last item in the column
        const buildL3Links = (linkItems, isOverflow = false) => linkItems.map((l3Item, index, arr) => {
            const { name: l3CategoryName, c_showSeparator: l3Separator } = l3Item
            const l3CmsReplacement = l3Item?.CategoryReplacement?.ContentLink?.Expanded
            const isLast = index === arr.length - 1 && (isOverflow || !l2HasOverflow)
            return {
                href: getCategoryUrl(l3Item, categories),
                text: l3CategoryName,
                defaultListStyle: (l3Separator && !isLast) ? { ...baseStyle.linkSeparator } : undefined,
                cmsCategoryReplacement: withForYouColor(l3Item, l3CmsReplacement),
                c_showSeparatorMobile: l3Item?.c_showSeparatorMobile,
                c_showSeparator: item?.c_showSeparator
            }
        })

        l2GridItems.push({
            key: `l2-${id}-main`,
            id,
            heading: l2Heading,
            header: l2CategoryName,
            links: buildL3Links(mainL2Items, false)
        })
        usedSlots++

        // Overflow column: continuation of the same L2 group, no header or heading
        if (l2HasOverflow && usedSlots < maxGroups) {
            l2GridItems.push({
                key: `l2-${id}-overflow`,
                id: `${id}-overflow`,
                heading: '',
                header: null,
                isOverflow: true,
                links: buildL3Links(overflowL2Items, true)
            })
            usedSlots++
        }
    }

    return (
        <Container {...baseStyle.popoverContainer}>
            <Box {...baseStyle.megaMenuGrid}>
                {/* L1 main column */}
                <Box {...baseStyle.megaMenuGridColumn}>
                    <Text {...baseStyle.level1Label}>
                        <FormattedMessage id="nav_level1.label" defaultMessage="Category" />
                    </Text>
                    <LinksList
                        variant="menuItem"
                        heading={l1Heading}
                        links={l1links}
                        onLinkClick={onClose}
                    />
                </Box>

                {/* L1 overflow column (continuation, no header) */}
                {l1HasOverflow && maxGroups > 1 && (
                    <Box {...baseStyle.megaMenuGridColumn}>
                        {/* Invisible spacer to keep items aligned with the main column */}
                        <Text {...baseStyle.level1Label} {...baseStyle.spacerLabel} aria-hidden="true">
                            &nbsp;
                        </Text>
                        <LinksList
                            variant="menuItem"
                            links={l1OverflowLinks}
                            onLinkClick={onClose}
                        />
                    </Box>
                )}

                {/* L2 separate columns */}
                {l2GridItems.map(({key, id, heading, header, isOverflow, links}) => (
                    <Box key={key} {...baseStyle.megaMenuGridColumn}>
                        {isOverflow ? (
                            <Text {...baseStyle.level1Label} {...baseStyle.spacerLabel} aria-hidden="true">
                                &nbsp;
                            </Text>
                        ) : (
                            header !== null && <Text {...baseStyle.level1Label}>{header}</Text>
                        )}
                        <LinksList
                            variant="menuItem"
                            key={`l2-link-list-${id}`}
                            heading={heading}
                            links={links}
                            onLinkClick={onClose}
                        />
                    </Box>
                ))}

                {/* Images right-aligned in the grid; colSpan resolved by shared helper
                    (ContentTile ColSpan per view type, legacy IllustrationWidth, or default).
                    The first image is pinned so the image group ends at the last grid column;
                    inner image keeps its natural aspect ratio without vertical stretching. */}
                {(() => {
                    if (!MegaMenuIlustrationBlockList?.length) return null
                    const totalImageCols = MegaMenuIlustrationBlockList.reduce(
                        (sum, block) => sum + getImageColSpan(block),
                        0
                    )
                    const firstImageStart = Math.max(
                        1,
                        TOTAL_GRID_COLUMNS - totalImageCols + 1
                    )
                    return MegaMenuIlustrationBlockList.map((block, index) => {
                        const expandedBlock = block?.ContentLink?.Expanded || block?.Expanded
                        const span = getImageColSpan(block)
                        return (
                            <Box
                                key={`default-img-${index}`}
                                gridColumn={
                                    index === 0
                                        ? `${firstImageStart} / span ${span}`
                                        : `span ${span}`
                                }
                                alignSelf="start"
                                justifySelf="end"
                                sx={{
                                    width: '100%',
                                    '& img': {
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block'
                                    }
                                }}
                            >
                                {renderComponentsSwitch(expandedBlock, {onClose})}
                            </Box>
                        )
                    })
                })()}
            </Box>
        </Container>
    )
}

DefaultMegaMenuContent.propTypes = {
    item: PropTypes.object,
    itemsKey: PropTypes.string,
    onClose: PropTypes.func,
    baseStyle: PropTypes.object,
    categories: PropTypes.object
}

export { DefaultMegaMenuContent }
